import React, { useState, useEffect } from 'react';
import { X, Trophy, Star, Crown, TrendingUp, Lock, Unlock } from 'lucide-react';
import { prestigeService } from '../services/prestigeService';
import Button from './ui/Button';
import { cn } from '../utils/cn';

/**
 * PrestigeModal - Displays prestige eligibility, benefits, and confirmation
 * Appears automatically when user reaches level 50 or can be triggered manually
 */
const PrestigeModal = ({ isOpen, onClose, onPrestige, userId }) => {
  const [loading, setLoading] = useState(true);
  const [prestigeStats, setPrestigeStats] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && userId) {
      loadPrestigeData();
    }
  }, [isOpen, userId]);

  const loadPrestigeData = async () => {
    try {
      setLoading(true);
      setError(null);
      const stats = await prestigeService.getPrestigeStats(userId);
      setPrestigeStats(stats);
    } catch (err) {
      console.error('Failed to load prestige data:', err);
      setError('Failed to load prestige information');
    } finally {
      setLoading(false);
    }
  };

  const handlePrestige = async () => {
    try {
      setConfirming(true);
      setError(null);
      
      const result = await prestigeService.executePrestige(userId);
      
      if (result.success) {
        // Call parent callback to refresh profile/UI
        if (onPrestige) onPrestige(result);
        // Close modal after brief delay to show success
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to execute prestige:', err);
      setError(err.message || 'Failed to prestige. Please try again.');
    } finally {
      setConfirming(false);
    }
  };

  if (!isOpen) return null;

  const getPrestigeBadgeInfo = () => {
    const currentPrestige = prestigeStats?.current_prestige || 0;
    const nextPrestige = currentPrestige + 1;
    
    const badges = {
      1: { name: 'Bronze Prestige', color: '#CD7F32', icon: Trophy },
      2: { name: 'Silver Prestige', color: '#C0C0C0', icon: Star },
      3: { name: 'Gold Prestige', color: '#FFD700', icon: Crown },
    };
    
    return badges[nextPrestige] || badges[1];
  };

  const badgeInfo = getPrestigeBadgeInfo();
  const BadgeIcon = badgeInfo.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-purple-600 to-indigo-600 p-6 rounded-t-xl">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors"
            aria-label="Close modal"
          >
            <X size={24} />
          </button>
          
          <div className="flex items-center gap-4">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{ backgroundColor: badgeInfo.color }}
            >
              <BadgeIcon size={32} className="text-white" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-white mb-1">
                Ready to Prestige?
              </h2>
              <p className="text-purple-100">
                Unlock the {badgeInfo.name} badge and elite drills
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
              <p className="text-gray-600 dark:text-gray-400 mt-4">Loading prestige data...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-red-800 dark:text-red-200">{error}</p>
            </div>
          ) : (
            <>
              {/* Stats Overview */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Current Prestige</div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    Level {prestigeStats?.current_prestige || 0}
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Lifetime XP</div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {prestigeStats?.lifetime_xp?.toLocaleString() || 0}
                  </div>
                </div>
              </div>

              {/* What Happens */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  What Happens When You Prestige?
                </h3>
                
                <div className="space-y-2">
                  <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <TrendingUp className="text-red-600 dark:text-red-400 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <div className="font-medium text-red-900 dark:text-red-100">Reset to Level 1</div>
                      <div className="text-sm text-red-700 dark:text-red-300">
                        Your current level and XP will reset, but your lifetime XP is preserved forever
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <Trophy className="text-green-600 dark:text-green-400 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <div className="font-medium text-green-900 dark:text-green-100">
                        Earn {badgeInfo.name} Badge
                      </div>
                      <div className="text-sm text-green-700 dark:text-green-300">
                        Permanent badge displayed on your profile and leaderboards
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <Unlock className="text-purple-600 dark:text-purple-400 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <div className="font-medium text-purple-900 dark:text-purple-100">
                        Unlock {prestigeStats?.next_prestige_drill_count || 5} Elite Drills
                      </div>
                      <div className="text-sm text-purple-700 dark:text-purple-300">
                        Access exclusive high-difficulty drills not available to regular players
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Preview of Unlockable Drills */}
              {prestigeStats?.next_prestige_drill_count > 0 && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <Lock size={20} className="text-gray-500" />
                    Unlockable Elite Drills
                  </h3>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    You'll unlock {prestigeStats.next_prestige_drill_count} new elite drills designed for {badgeInfo.name} members
                  </div>
                </div>
              )}

              {/* Warning */}
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <TrendingUp className="text-yellow-600 dark:text-yellow-400 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <div className="font-medium text-yellow-900 dark:text-yellow-100 mb-1">
                      This Action Cannot Be Undone
                    </div>
                    <div className="text-sm text-yellow-700 dark:text-yellow-300">
                      Once you prestige, you'll start back at Level 1. Your lifetime XP and badges are permanent, 
                      but you'll need to level up again to reach new milestones.
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="flex-1"
                  disabled={confirming}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handlePrestige}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                  disabled={confirming || !prestigeStats?.can_prestige}
                >
                  {confirming ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Prestiging...
                    </>
                  ) : (
                    <>
                      <Trophy size={18} className="mr-2" />
                      Prestige Now
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrestigeModal;
